



// Permission utility functions for role-based access control

/**
 * Get user's permissions from localStorage
 */
export const getUserPermissions = () => {
    try {
        const permissions = localStorage.getItem("userPermissions")
        return permissions ? JSON.parse(permissions) : []
    } catch (error) {
        console.error("Error getting user permissions:", error)
        return []
    }
}

/**
 * Get user's role from localStorage
 */
export const getUserRole = () => {
    return localStorage.getItem("userRole") || null
}

/**
 * Check if user is superAdmin
 */
export const isSuperAdmin = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}")
    return user.user_type === "superAdmin" || getUserRole() === "superAdmin"
}

/**
 * Check if user has a specific permission
 */
export const hasPermission = (permission) => {
    // SuperAdmin has all permissions
    if (isSuperAdmin()) {
        return true
    }

    const userPermissions = getUserPermissions()
    return userPermissions.includes(permission)
}

/**
 * Check if user has any of the specified permissions
 */
export const hasAnyPermission = (permissions) => {
    // SuperAdmin has all permissions
    if (isSuperAdmin()) {
        return true
    }

    const userPermissions = getUserPermissions()
    return permissions.some((permission) => userPermissions.includes(permission))
}

/**
 * Check if user has all of the specified permissions
 */
export const hasAllPermissions = (permissions) => {
    // SuperAdmin has all permissions
    if (isSuperAdmin()) {
        return true
    }

    const userPermissions = getUserPermissions()
    return permissions.every((permission) => userPermissions.includes(permission))
}

/**
 * Filter menu items based on user permissions
 */
export const filterMenuByPermissions = (menuItems) => {
    // SuperAdmin sees all menu items
    if (isSuperAdmin()) {
        return menuItems
    }

    const userPermissions = getUserPermissions()
    const filteredItems = menuItems
        .map((item) => {
            if (item.type === "separator") {
                return item
            }

            if (!item.permission) {
                return item.hasSubmenu && item.submenuItems
                    ? {
                        ...item,
                        submenuItems: item.submenuItems.filter((subItem) => {
                            if (!subItem.permission) {
                                return true
                            }
                            return userPermissions.includes(subItem.permission)
                        }),
                    }
                    : item
            }

            if (!userPermissions.includes(item.permission)) {
                return null
            }

            if (item.hasSubmenu && item.submenuItems) {
                const filteredSubmenuItems = item.submenuItems.filter((subItem) => {
                    if (!subItem.permission) {
                        return true
                    }
                    return userPermissions.includes(subItem.permission)
                })

                if (!filteredSubmenuItems.length) {
                    return null
                }

                return {
                    ...item,
                    submenuItems: filteredSubmenuItems,
                }
            }

            return item
        })
        .filter(Boolean)

    return filteredItems.filter((item, index) => {
        if (item.type !== "separator") {
            return true
        }

        const itemsInCurrentGroup = []
        for (let i = index + 1; i < filteredItems.length; i += 1) {
            const nextItem = filteredItems[i]
            if (nextItem.type === "separator") {
                break
            }
            itemsInCurrentGroup.push(nextItem)
        }

        return itemsInCurrentGroup.length > 0
    })
}

// Permission constants matching the backend permissions
export const PERMISSIONS = {
    // Dashboard
    DASHBOARD_VIEW: "dashboard:view",

    // Library
    LIBRARY_VIEW: "library:view",
    LIBRARY_CREATE: "library:create",
    LIBRARY_EDIT: "library:edit",
    LIBRARY_DELETE: "library:delete",

    // Tenure
    TENURE_VIEW: "tenure:view",
    TENURE_CREATE: "tenure:create",
    TENURE_EDIT: "tenure:edit",
    TENURE_DELETE: "tenure:delete",

    // Mentor
    MENTOR_VIEW: "mentor:view",
    MENTOR_CREATE: "mentor:create",
    MENTOR_EDIT: "mentor:edit",
    MENTOR_DELETE: "mentor:delete",

    // E-book
    EBOOK_VIEW: "ebook:view",
    EBOOK_CREATE: "ebook:create",
    EBOOK_EDIT: "ebook:edit",
    EBOOK_DELETE: "ebook:delete",

    // Users
    USER_VIEW: "user:view",
    USER_CREATE: "user:create",
    USER_EDIT: "user:edit",
    USER_DELETE: "user:delete",

    // Organization
    ORGANIZATION_VIEW: "organization:view",
    ORGANIZATION_CREATE: "organization:create",
    ORGANIZATION_EDIT: "organization:edit",
    ORGANIZATION_DELETE: "organization:delete",

    // Section
    SECTION_VIEW: "section:view",
    SECTION_CREATE: "section:create",
    SECTION_EDIT: "section:edit",
    SECTION_DELETE: "section:delete",

    // Subject
    SUBJECT_VIEW: "subject:view",
    SUBJECT_CREATE: "subject:create",
    SUBJECT_EDIT: "subject:edit",
    SUBJECT_DELETE: "subject:delete",

    // Topic
    TOPIC_VIEW: "topic:view",
    TOPIC_CREATE: "topic:create",
    TOPIC_EDIT: "topic:edit",
    TOPIC_DELETE: "topic:delete",

    // Subtopic
    SUBTOPIC_VIEW: "subtopic:view",
    SUBTOPIC_CREATE: "subtopic:create",
    SUBTOPIC_EDIT: "subtopic:edit",
    SUBTOPIC_DELETE: "subtopic:delete",

    // Exam
    EXAM_VIEW: "exam:view",
    EXAM_CREATE: "exam:create",
    EXAM_EDIT: "exam:edit",
    EXAM_DELETE: "exam:delete",
    EXAM_DUPLICATE: "exam:duplicate",
    EXAM_EXPORT: "exam:export",
    EXAM_ASSIGN_QUESTION: "exam:assign_question",

    // Package
    PACKAGE_VIEW: "package:view",
    PACKAGE_CREATE: "package:create",
    PACKAGE_EDIT: "package:edit",
    PACKAGE_DELETE: "package:delete",
    PACKAGE_EXPORT: "package:export",

    // Question
    QUESTION_VIEW: "question:view",
    QUESTION_CREATE: "question:create",
    QUESTION_EDIT: "question:edit",
    QUESTION_DELETE: "question:delete",
    QUESTION_BULK_UPLOAD: "question:bulk_upload",
    QUESTION_EXPORT: "question:export",
    QUESTION_PARAGRAPH_CREATE: "question:paragraph_create",

    // Language
    LANGUAGE_VIEW: "language:view",
    LANGUAGE_CREATE: "language:create",
    LANGUAGE_EDIT: "language:edit",
    LANGUAGE_DELETE: "language:delete",
    LANGUAGE_EXPORT: "language:export",

    // Order
    FINANCE_VIEW: "finance:view",
    // Order
    ORDER_VIEW: "order:view",
    ORDER_CREATE: "order:create",
    ORDER_EDIT: "order:edit",
    ORDER_DELETE: "order:delete",
    ORDER_STATUS: "order:status",
    LIBRARY_BOOKING_VIEW: "order:booking_view",
    PACKAGE_PURCHASE_VIEW: "order:mock_view",
    EBOOK_PURCHASE_VIEW: "order:ebook_view",

    // Report
    REPORT_VIEW: "report:view",
    LIBRARY_REPORT_VIEW: "report:library_report_view",
    LIBRARY_REPORT_EXPORT: "report:library_report_export",
    PACKAGE_REPORT_VIEW: "report:package_report_view",
    PACKAGE_REPORT_EXPORT: "report:package_report_export",
    EBOOK_REPORT_VIEW: "report:ebook_report_view",
    EBOOK_REPORT_EXPORT: "report:ebook_report_export",

    // Referral
    REFERRAL_VIEW: "referral:view",
    REFERRAL_CREATE: "referral:create",
    REFERRAL_EDIT: "referral:edit",
    REFERRAL_DELETE: "referral:delete",
    REFERRAL_EXPORT: "referral:export",

    // Coupon
    COUPON_VIEW: "coupon:view",
    COUPON_CREATE: "coupon:create",
    COUPON_EDIT: "coupon:edit",
    COUPON_DELETE: "coupon:delete",

    // Banner
    BANNER_VIEW: "banner:view",
    BANNER_CREATE: "banner:create",
    BANNER_EDIT: "banner:edit",
    BANNER_DELETE: "banner:delete",

    // Settings
    SETTINGS_VIEW: "settings:view",
    SETTINGS_EDIT: "settings:edit",

    // Testimonial
    TESTIMONIAL_VIEW: "testimonial:view",
    TESTIMONIAL_CREATE: "testimonial:create",
    TESTIMONIAL_EDIT: "testimonial:edit",
    TESTIMONIAL_DELETE: "testimonial:delete",

    // Contact Query
    CONTACT_QUERY_VIEW: "contact_query:view",
    CONTACT_QUERY_EDIT: "contact_query:edit",
    CONTACT_QUERY_DELETE: "contact_query:delete",

    // FAQ
    FAQ_VIEW: "faq:view",
    FAQ_CREATE: "faq:create",
    FAQ_EDIT: "faq:edit",
    FAQ_DELETE: "faq:delete",

    // Role
    ROLE_VIEW: "role:view",
    ROLE_CREATE: "role:create",
    ROLE_EDIT: "role:edit",
    ROLE_DELETE: "role:delete",
}


export const PERMISSION_GROUPS = [
    {
        name: "Dashboard",
        permissions: [{ value: PERMISSIONS.DASHBOARD_VIEW, label: "View Dashboard" }],
    },
    {
        name: "Library Management",
        permissions: [
            { value: PERMISSIONS.LIBRARY_VIEW, label: "View Libraries" },
            { value: PERMISSIONS.LIBRARY_CREATE, label: "Create Library" },
            { value: PERMISSIONS.LIBRARY_EDIT, label: "Edit Library" },
            { value: PERMISSIONS.LIBRARY_DELETE, label: "Delete Library" },
        ],
    },
    {
        name: "Library Tenure",
        permissions: [
            { value: PERMISSIONS.TENURE_VIEW, label: "View Tenures" },
            { value: PERMISSIONS.TENURE_CREATE, label: "Create Tenure" },
            { value: PERMISSIONS.TENURE_EDIT, label: "Edit Tenure" },
            { value: PERMISSIONS.TENURE_DELETE, label: "Delete Tenure" },
        ],
    },
    {
        name: "Mentor Management",
        permissions: [
            { value: PERMISSIONS.MENTOR_VIEW, label: "View Mentors" },
            { value: PERMISSIONS.MENTOR_CREATE, label: "Create Mentor" },
            { value: PERMISSIONS.MENTOR_EDIT, label: "Edit Mentor" },
            { value: PERMISSIONS.MENTOR_DELETE, label: "Delete Mentor" },
        ],
    },
    {
        name: "E-Book Management",
        permissions: [
            { value: PERMISSIONS.EBOOK_VIEW, label: "View E-Books" },
            { value: PERMISSIONS.EBOOK_CREATE, label: "Create E-Book" },
            { value: PERMISSIONS.EBOOK_EDIT, label: "Edit E-Book" },
            { value: PERMISSIONS.EBOOK_DELETE, label: "Delete E-Book" },
        ],
    },
    {
        name: "User Management",
        permissions: [
            { value: PERMISSIONS.USER_VIEW, label: "View Users" },
            { value: PERMISSIONS.USER_CREATE, label: "Create User" },
            { value: PERMISSIONS.USER_EDIT, label: "Edit User" },
            { value: PERMISSIONS.USER_DELETE, label: "Delete User" },
        ],
    },
    {
        name: "Organization Management",
        permissions: [
            { value: PERMISSIONS.ORGANIZATION_VIEW, label: "View Organizations" },
            { value: PERMISSIONS.ORGANIZATION_CREATE, label: "Create Organization" },
            { value: PERMISSIONS.ORGANIZATION_EDIT, label: "Edit Organization" },
            { value: PERMISSIONS.ORGANIZATION_DELETE, label: "Delete Organization" },
        ],
    },
    {
        name: "Section Management",
        permissions: [
            { value: PERMISSIONS.SECTION_VIEW, label: "View Sections" },
            { value: PERMISSIONS.SECTION_CREATE, label: "Create Section" },
            { value: PERMISSIONS.SECTION_EDIT, label: "Edit Section" },
            { value: PERMISSIONS.SECTION_DELETE, label: "Delete Section" },
        ],
    },
    {
        name: "Subject Management",
        permissions: [
            { value: PERMISSIONS.SUBJECT_VIEW, label: "View Subjects" },
            { value: PERMISSIONS.SUBJECT_CREATE, label: "Create Subject" },
            { value: PERMISSIONS.SUBJECT_EDIT, label: "Edit Subject" },
            { value: PERMISSIONS.SUBJECT_DELETE, label: "Delete Subject" },
        ],
    },
    {
        name: "Topic Management",
        permissions: [
            { value: PERMISSIONS.TOPIC_VIEW, label: "View Topics" },
            { value: PERMISSIONS.TOPIC_CREATE, label: "Create Topic" },
            { value: PERMISSIONS.TOPIC_EDIT, label: "Edit Topic" },
            { value: PERMISSIONS.TOPIC_DELETE, label: "Delete Topic" },
        ],
    },
    {
        name: "Sub Topic Management",
        permissions: [
            { value: PERMISSIONS.SUBTOPIC_VIEW, label: "View Sub Topics" },
            { value: PERMISSIONS.SUBTOPIC_CREATE, label: "Create Sub Topic" },
            { value: PERMISSIONS.SUBTOPIC_EDIT, label: "Edit Sub Topic" },
            { value: PERMISSIONS.SUBTOPIC_DELETE, label: "Delete Sub Topic" },
        ],
    },
    {
        name: "Exam Management",
        permissions: [
            { value: PERMISSIONS.EXAM_VIEW, label: "View Exams" },
            { value: PERMISSIONS.EXAM_CREATE, label: "Create Exam" },
            { value: PERMISSIONS.EXAM_EDIT, label: "Edit Exam" },
            { value: PERMISSIONS.EXAM_DELETE, label: "Delete Exam" },
            { value: PERMISSIONS.EXAM_DUPLICATE, label: "Duplicate Exam" },
            { value: PERMISSIONS.EXAM_EXPORT, label: "Export Exam" },
            { value: PERMISSIONS.EXAM_ASSIGN_QUESTION, label: "Assign Question In Exam" },
        ],
    },
    {
        name: "Package Management",
        permissions: [
            { value: PERMISSIONS.PACKAGE_VIEW, label: "View Packages" },
            { value: PERMISSIONS.PACKAGE_CREATE, label: "Create Package" },
            { value: PERMISSIONS.PACKAGE_EDIT, label: "Edit Package" },
            { value: PERMISSIONS.PACKAGE_DELETE, label: "Delete Package" },
            { value: PERMISSIONS.PACKAGE_EXPORT, label: "Export Package" },
        ],
    },
    {
        name: "Contact Query Management",
        permissions: [
            { value: PERMISSIONS.CONTACT_QUERY_VIEW, label: "View Contact Queries" },
            { value: PERMISSIONS.CONTACT_QUERY_EDIT, label: "Update Contact Query Status" },
            { value: PERMISSIONS.CONTACT_QUERY_DELETE, label: "Delete Contact Queries" },
        ],
    },
    {
        name: "FAQ Management",
        permissions: [
            { value: PERMISSIONS.FAQ_VIEW, label: "View FAQs" },
            { value: PERMISSIONS.FAQ_CREATE, label: "Create FAQ" },
            { value: PERMISSIONS.FAQ_EDIT, label: "Edit FAQ" },
            { value: PERMISSIONS.FAQ_DELETE, label: "Delete FAQ" },
        ],
    },
    {
        name: "Question Management",
        permissions: [
            { value: PERMISSIONS.QUESTION_VIEW, label: "View Questions" },
            { value: PERMISSIONS.QUESTION_CREATE, label: "Create Question" },
            { value: PERMISSIONS.QUESTION_EDIT, label: "Edit Question" },
            { value: PERMISSIONS.QUESTION_DELETE, label: "Delete Question" },
            { value: PERMISSIONS.QUESTION_BULK_UPLOAD, label: "Bulk Upload Question" },
            { value: PERMISSIONS.QUESTION_EXPORT, label: "Export Question" },
            { value: PERMISSIONS.QUESTION_PARAGRAPH_CREATE, label: "Create Paragraph" },
        ],
    },
    {
        name: "Language Management",
        permissions: [
            { value: PERMISSIONS.LANGUAGE_VIEW, label: "View Languages" },
            { value: PERMISSIONS.LANGUAGE_CREATE, label: "Create Language" },
            { value: PERMISSIONS.LANGUAGE_EDIT, label: "Edit Language" },
            { value: PERMISSIONS.LANGUAGE_DELETE, label: "Delete Language" },
            { value: PERMISSIONS.LANGUAGE_EXPORT, label: "Export Language" },
        ],
    },
    {
        name: "Finance Management",
        permissions: [
            { value: PERMISSIONS.FINANCE_VIEW, label: "View Finance Section" },
        ],
    },
    {
        name: "Order Management",
        permissions: [
            { value: PERMISSIONS.ORDER_VIEW, label: "View Orders" },
            { value: PERMISSIONS.ORDER_STATUS, label: "Order Status" },
            { value: PERMISSIONS.LIBRARY_BOOKING_VIEW, label: "View Library Bookings" },
            { value: PERMISSIONS.PACKAGE_PURCHASE_VIEW, label: "View Package Purchases" },
            { value: PERMISSIONS.EBOOK_PURCHASE_VIEW, label: "View E-Book Purchases" },
        ],
    },
    {
        name: "Reports",
        permissions: [
            { value: PERMISSIONS.REPORT_VIEW, label: "View Reports" },
            { value: PERMISSIONS.LIBRARY_REPORT_VIEW, label: "View Library Reports" },
            { value: PERMISSIONS.LIBRARY_REPORT_EXPORT, label: "Export Library Reports" },
            { value: PERMISSIONS.PACKAGE_REPORT_VIEW, label: "View Package Reports" },
            { value: PERMISSIONS.PACKAGE_REPORT_EXPORT, label: "Export Package Reports" },
            { value: PERMISSIONS.EBOOK_REPORT_VIEW, label: "View E-Book Reports" },
            { value: PERMISSIONS.EBOOK_REPORT_EXPORT, label: "Export E-Book Reports" },
        ],
    },
    {
        name: "Referral Management",
        permissions: [
            { value: PERMISSIONS.REFERRAL_VIEW, label: "View Referrals" },
            { value: PERMISSIONS.REFERRAL_EXPORT, label: "Export Referrals " },
        ],
    },
    {
        name: "Coupon Management",
        permissions: [
            { value: PERMISSIONS.COUPON_VIEW, label: "View Coupons" },
            { value: PERMISSIONS.COUPON_CREATE, label: "Create Coupon" },
            { value: PERMISSIONS.COUPON_EDIT, label: "Edit Coupon" },
            { value: PERMISSIONS.COUPON_DELETE, label: "Delete Coupon" },
        ],
    },
    {
        name: "Banner Management",
        permissions: [
            { value: PERMISSIONS.BANNER_VIEW, label: "View Banners" },
            { value: PERMISSIONS.BANNER_CREATE, label: "Create Banner" },
            { value: PERMISSIONS.BANNER_EDIT, label: "Edit Banner" },
            { value: PERMISSIONS.BANNER_DELETE, label: "Delete Banner" },
        ],
    },
    {
        name: "Settings",
        permissions: [
            { value: PERMISSIONS.SETTINGS_VIEW, label: "View Settings" },
            { value: PERMISSIONS.SETTINGS_EDIT, label: "Edit Settings" },
        ],
    },
    {
        name: "Testimonial Management",
        permissions: [
            { value: PERMISSIONS.TESTIMONIAL_VIEW, label: "View Testimonials" },
            { value: PERMISSIONS.TESTIMONIAL_CREATE, label: "Create Testimonial" },
            { value: PERMISSIONS.TESTIMONIAL_EDIT, label: "Edit Testimonial" },
            { value: PERMISSIONS.TESTIMONIAL_DELETE, label: "Delete Testimonial" },
        ],
    },
    {
        name: "Role & Permission Management",
        permissions: [
            { value: PERMISSIONS.ROLE_VIEW, label: "View Roles" },
            { value: PERMISSIONS.ROLE_CREATE, label: "Create Role" },
            { value: PERMISSIONS.ROLE_EDIT, label: "Edit Role" },
            { value: PERMISSIONS.ROLE_DELETE, label: "Delete Role" },
        ],
    },
]
